import { useState } from "react";
import { Navigate } from "react-router-dom";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

export function LoginPage() {
  const { session, signIn, requiresNewPassword, completeNewPassword } =
    useAuth();
  const { t } = useI18n();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (session) return <Navigate to="/" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signIn(username, password);
    } catch {
      setError(t("signInFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function setPermanentPassword(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t("passwordMismatch"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await completeNewPassword(newPassword);
    } catch {
      setError(t("passwordSetupFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <div className="login-language">
        <LanguageSwitch />
      </div>
      <section className="login-panel">
        <div className="login-brand">
          <img
            className="login-logo"
            src="/bridge-ai-logo.png"
            alt={t("appTagline")}
          />
        </div>
        <div className="login-copy">
          <span className="eyebrow">{t("appName")}</span>
          <h1>{requiresNewPassword ? t("setPassword") : t("welcome")}</h1>
          <p>{requiresNewPassword ? t("setPasswordHelp") : t("loginHelp")}</p>
        </div>
        <form onSubmit={requiresNewPassword ? setPermanentPassword : submit}>
          {requiresNewPassword ? (
            <>
              <label>
                {t("newPassword")}
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                />
              </label>
              <label>
                {t("confirmPassword")}
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label>
                {t("username")}
                <input
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                />
              </label>
              <label>
                {t("password")}
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </label>
            </>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <button
            className="primary-button full-width"
            type="submit"
            disabled={busy}
          >
            {requiresNewPassword ? t("savePassword") : t("signIn")}
          </button>
        </form>
        <p className="login-footer">{t("passwordReset")}</p>
      </section>
    </main>
  );
}
